/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import { isPrimitive as isNonNegativeInteger } from '@stdlib/assert-is-nonnegative-integer';
import isndarrayLike from '@stdlib/assert-is-ndarray-like';
import isPlainObject from '@stdlib/assert-is-plain-object';
import { primitives as isNonNegativeIntegerArray } from '@stdlib/assert-is-nonnegative-integer-array';
import isEmptyCollection from '@stdlib/assert-is-empty-collection';
import hasOwnProp from '@stdlib/assert-has-own-property';
import { factory as contains } from '@stdlib/array-base-assert-contains';
import shape2strides from '@stdlib/ndarray-base-shape2strides';
import buffer from '@stdlib/ndarray-base-buffer';
import numel from '@stdlib/ndarray-base-numel';
import getDType from '@stdlib/ndarray-dtype';
import getShape from '@stdlib/ndarray-shape';
import getOrder from '@stdlib/ndarray-order';
import ndarray from '@stdlib/ndarray-ctor';
import dtypes from '@stdlib/ndarray-dtypes';
import join from '@stdlib/array-base-join';
import format from '@stdlib/error-tools-fmtprodmsg';


// VARIABLES //

var DTYPES = dtypes( 'numeric_and_generic' );
var isDataType = contains( DTYPES );


// MAIN //

/**
* Creates a zero-filled ndarray having the same shape and data type as a provided ndarray.
*
* @param {ndarray} x - input array
* @param {Options} [options] - function options
* @param {*} [options.dtype] - output array data type (overrides the input array's inferred data type)
* @param {string} [options.order] - specifies whether the output array should be 'row-major' (C-style) or 'column-major' (Fortran-style) (overrides the input array's inferred order)
* @param {(NonNegativeIntegerArray|NonNegativeInteger)} [options.shape] - output array shape (overrides the input array's inferred shape)
* @param {string} [options.mode="throw"] - specifies how to handle indices which exceed array dimensions
* @param {ArrayLikeObject<string>} [options.submode=[options.mode]] - specifies how to handle subscripts which exceed array dimensions on a per dimension basis
* @param {boolean} [options.readonly=false] - boolean indicating whether an array should be read-only
* @throws {TypeError} first argument must have a recognized data type
* @throws {TypeError} options argument must be an object
* @throws {TypeError} `dtype` option must be a supported data type
* @throws {TypeError} `order` option must be a supported order
* @throws {TypeError} `shape` option must be either a nonnegative integer or an array of nonnegative integers
* @throws {TypeError} must provide valid options
* @returns {ndarray} ndarray
*
* @example
* import getShape from '@stdlib/ndarray-shape';
* import getDType from '@stdlib/ndarray-dtype';
* import zeros from '@stdlib/ndarray-zeros';
*
* var x = zeros( [ 2, 2 ] );
* // returns <ndarray>[ [ 0.0, 0.0 ], [ 0.0, 0.0 ] ]
*
* var y = zerosLike( x );
* // returns <ndarray>[ [ 0.0, 0.0 ], [ 0.0, 0.0 ] ]
*
* var sh = getShape( y );
* // returns [ 2, 2 ]
*
* var dt = String( getDType( y ) );
* // returns 'float64'
*/
function zerosLike( x ) {
	var options;
	var dtype;
	var order;
	var ndims;
	var opts;
	var len;
	var flg;
	var st;
	var sh;

	if ( !isndarrayLike( x ) ) {
		throw new TypeError( format( '0kH5t', x ) );
	}
	flg = false;
	opts = {};
	if ( arguments.length > 1 ) {
		options = arguments[ 1 ];
		if ( !isPlainObject( options ) ) {
			throw new TypeError( format( '0kH2V', options ) );
		}
		if ( hasOwnProp( options, 'dtype' ) ) {
			dtype = options.dtype;
			if ( !isDataType( dtype ) ) {
				throw new TypeError( format( '0kH4S', 'dtype', join( DTYPES, '", "' ), dtype ) );
			}
			flg = true;
		} else {
			dtype = getDType( x );
		}
		if ( hasOwnProp( options, 'shape' ) ) {
			sh = options.shape;
			if ( isNonNegativeInteger( sh ) ) {
				sh = [ sh ];
			} else if ( !isNonNegativeIntegerArray( sh ) && !isEmptyCollection( sh ) ) { // eslint-disable-line max-len
				throw new TypeError( format( '0kHDs', 'shape', sh ) );
			}
		} else {
			sh = getShape( x );
		}
		if ( hasOwnProp( options, 'order' ) ) {
			order = options.order;
		} else {
			order = getOrder( x );
		}
		if ( hasOwnProp( options, 'mode' ) ) {
			opts.mode = options.mode;
		}
		if ( hasOwnProp( options, 'submode' ) ) {
			opts.submode = options.submode;
		}
		if ( hasOwnProp( options, 'readonly' ) ) {
			opts.readonly = options.readonly;
		}
	} else {
		dtype = getDType( x );
		sh = getShape( x );
		order = getOrder( x );
	}
	if ( !flg && !isDataType( dtype ) ) {
		throw new TypeError( format( 'invalid argument. First argument must have one of the following data types: "%s". Value: `%s`.', join( DTYPES, '", "' ), dtype ) );
	}
	ndims = sh.length;
	if ( ndims > 0 ) {
		len = numel( sh );
		st = shape2strides( sh, order );
	} else {
		// For 0-dimensional arrays, the buffer should contain a single element...
		len = 1;
		st = [ 0 ];
	}
	return new ndarray( dtype, buffer( dtype, len ), sh, st, 0, order, opts );
}


// EXPORTS //

export default zerosLike;
